import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { BookOpen, Check, ChevronLeft, ImagePlus, LogOut, Save, Send, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const fieldCls = "w-full bg-argali-obsidian border border-argali-brown/40 text-argali-ivory text-sm px-3 py-2 outline-none focus:border-argali-bronze";
const labelCls = "text-[0.6rem] uppercase tracking-[0.16em] text-argali-sandstone/60 mb-1 block";
const secondaryBtn = "border border-argali-brown/50 text-argali-sandstone hover:text-argali-gold hover:border-argali-bronze px-4 py-2 text-xs uppercase tracking-[0.12em] transition-colors";
const managerRole = (user) => user?.role === "owner" || user?.role === "admin";

const TYPE_LABELS = {
  trip: "Поездка / экскурсия",
  competition: "Соревнование",
  achievement: "Достижение",
  story: "История",
  gallery: "Фотогалерея",
};
const SOURCE_LABELS = {
  argali: "АРГАЛИ",
  other_company: "Другая компания",
  personal: "Личное / самостоятельное",
};
const STATUS_LABELS = { draft: "Черновик", pending: "На согласовании", approved: "Опубликовано", rejected: "Отклонено" };

const emptyForm = () => ({
  id: null,
  post_type: "trip",
  title_ru: "",
  title_en: "",
  body_ru: "",
  body_en: "",
  event_date: "",
  location: "",
  organization: "",
  source_context: "argali",
  source_company_name: "",
  rights_confirmed: false,
  status: "draft",
  admin_note: "",
  photos: [],
});

const PrivatePhoto = ({ photo }) => {
  const [src, setSrc] = useState("");
  useEffect(() => {
    let alive = true;
    let objectUrl = "";
    const token = localStorage.getItem("argali_token");
    fetch(photo.url, { headers: token ? { Authorization: `Bearer ${token}` } : {} })
      .then((r) => { if (!r.ok) throw new Error("photo"); return r.blob(); })
      .then((blob) => {
        if (!alive) return;
        objectUrl = URL.createObjectURL(blob);
        setSrc(objectUrl);
      })
      .catch(() => {});
    return () => { alive = false; if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [photo.url]);
  return src ? <img src={src} alt={photo.caption_ru || photo.original_name || "Фото"} className="w-full h-full object-cover" /> : <div className="w-full h-full bg-argali-brown/20" />;
};

export const GuideJournalShortcut = () => {
  const { user } = useAuth();
  if (!user || !user.role) return null;
  return (
    <Link to="/admin/journal" className="fixed z-[70] right-5 md:right-8 top-[132px] border border-argali-bronze/60 bg-argali-obsidian/95 text-argali-gold hover:text-argali-ivory hover:border-argali-gold px-4 py-2.5 text-[0.66rem] uppercase tracking-[0.15em] flex items-center gap-2 shadow-xl">
      <BookOpen size={15} /> {managerRole(user) ? "Публикации гидов" : "Моя страница"}
    </Link>
  );
};

export default function AdminGuideJournal() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const manager = managerRole(user);
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(emptyForm());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [reviewNote, setReviewNote] = useState("");

  useEffect(() => { if (user === false) navigate("/login"); }, [user, navigate]);

  const load = async () => {
    if (!user?.role) return;
    setLoading(true);
    try {
      const { data } = await api.get("/guide-journal");
      setItems(data || []);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [user?.role]); // eslint-disable-line react-hooks/exhaustive-deps

  const select = (post) => {
    setForm({ ...emptyForm(), ...post, rights_confirmed: !!post.rights_confirmed, photos: post.photos || [] });
    setReviewNote(post.admin_note || "");
  };
  const setF = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  const save = async () => {
    if (manager) return;
    setSaving(true);
    try {
      const payload = { ...form };
      delete payload.id; delete payload.photos; delete payload.status; delete payload.admin_note;
      let result;
      if (form.id) result = await api.patch(`/guide-journal/${form.id}`, payload);
      else result = await api.post("/guide-journal", payload);
      const next = { ...form, ...result.data, photos: result.data.photos || form.photos || [] };
      setForm(next);
      toast.success(form.id ? "Черновик сохранён" : "Черновик создан — теперь можно добавить фотографии");
      await load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const upload = async (file) => {
    if (!form.id || !file) return;
    setUploading(true);
    try {
      const token = localStorage.getItem("argali_token");
      const fd = new FormData();
      fd.append("photo", file);
      const response = await fetch(`/api/guide-journal/${form.id}/photos`, {
        method: "POST",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: fd,
      });
      let data = {}; try { data = await response.json(); } catch {}
      if (!response.ok) throw Object.assign(new Error(data.detail || "Upload failed"), { response: { data } });
      setForm((prev) => ({ ...prev, photos: [...(prev.photos || []), data] }));
      toast.success("Фотография добавлена");
      await load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail || err.message)); }
    finally { setUploading(false); }
  };

  const removePhoto = async (photo) => {
    if (!window.confirm("Удалить эту фотографию?")) return;
    try {
      await api.delete(`/guide-journal/photos/${photo.id}`);
      setForm((prev) => ({ ...prev, photos: (prev.photos || []).filter((p) => p.id !== photo.id) }));
      toast.success("Фотография удалена");
      await load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const submit = async () => {
    if (!form.id) return;
    try {
      await api.post(`/guide-journal/${form.id}/submit`, {});
      toast.success("Отправлено администратору на согласование");
      setForm(emptyForm()); await load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const review = async (status) => {
    if (!form.id || !manager) return;
    try {
      await api.post(`/guide-journal/${form.id}/review`, { status, admin_note: reviewNote });
      toast.success(status === "approved" ? "Публикация одобрена и стала публичной" : "Публикация отклонена");
      setForm(emptyForm()); setReviewNote(""); await load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const removePost = async () => {
    if (!form.id || !window.confirm("Удалить публикацию и её фотографии?")) return;
    try {
      await api.delete(`/guide-journal/${form.id}`);
      toast.success("Публикация удалена"); setForm(emptyForm()); await load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const pendingCount = useMemo(() => items.filter((p) => p.status === "pending").length, [items]);

  if (user === null) return <div className="min-h-screen" style={{ background: "#11110F" }} />;
  if (!user?.role) return null;

  return (
    <div className="min-h-screen" style={{ background: "#11110F" }}>
      <header className="flex items-center justify-between px-5 md:px-10 h-[72px] border-b border-argali-brown/30">
        <div className="flex items-center gap-2.5">
          <img src="/brand/argali_emblem_ru.png" alt="Логотип АРГАЛИ" width="543" height="501" className="h-8 w-auto" />
          <span className="font-serif text-xl md:text-2xl tracking-[0.2em] text-argali-ivory">АРГАЛИ</span>
          <span className="eyebrow ml-2 hidden sm:inline">{manager ? `Публикации${pendingCount ? ` · ${pendingCount} новых` : ""}` : "Моя страница"}</span>
        </div>
        <div className="flex items-center gap-3 md:gap-5">
          <Link to="/admin" className="flex items-center gap-1.5 text-argali-sandstone/80 hover:text-argali-gold text-sm"><ChevronLeft size={16} /> Кабинет</Link>
          <button onClick={() => { logout(); navigate("/login"); }} className="flex items-center gap-1.5 text-argali-sandstone/80 hover:text-argali-gold text-sm"><LogOut size={16} /> Выйти</button>
        </div>
      </header>

      <main className="max-w-[1400px] mx-auto px-5 md:px-10 py-10">
        <div className="mb-8">
          <p className="eyebrow mb-2">{manager ? "Согласование контента гидов" : "Личный журнал гида"}</p>
          <h1 className="font-serif text-3xl md:text-4xl text-argali-ivory">{manager ? "Истории, поездки и фотографии" : "Мои истории и фотографии"}</h1>
          <p className="text-sm text-argali-sandstone/55 mt-2 max-w-3xl">Публикации другой компании отмечаются отдельно и не выдаются за поездки АРГАЛИ. Материалы становятся публичными только после одобрения администратора.</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[320px_1fr] gap-8">
          <aside>
            {!manager && <button onClick={() => setForm(emptyForm())} className="btn-bronze w-full mb-4">+ Новая публикация</button>}
            <div className="space-y-2 max-h-[72vh] overflow-y-auto no-scrollbar" data-lenis-prevent>
              {loading && <p className="text-argali-sandstone/50">Загрузка…</p>}
              {!loading && items.length === 0 && <p className="text-argali-sandstone/50">Публикаций пока нет.</p>}
              {items.map((p) => (
                <button key={p.id} onClick={() => select(p)} className="w-full text-left border border-argali-brown/30 hover:border-argali-bronze p-4 transition-colors">
                  <span className="text-[0.58rem] uppercase tracking-[0.14em] text-argali-gold">{TYPE_LABELS[p.post_type]} · {STATUS_LABELS[p.status]}</span>
                  <span className="block font-serif text-lg text-argali-ivory mt-1">{p.title_ru}</span>
                  {manager && <span className="block text-xs text-argali-sandstone/50 mt-1">Гид: {p.guide_name_ru || p.guide_name}</span>}
                  <span className="block text-xs text-argali-sandstone/40 mt-1">Фото: {(p.photos || []).length}</span>
                </button>
              ))}
            </div>
          </aside>

          <section className="border border-argali-brown/30 p-6 md:p-8">
            {manager && !form.id ? (
              <div className="min-h-[360px] flex items-center justify-center text-center text-argali-sandstone/45">Выберите публикацию слева для проверки.</div>
            ) : (
              <>
                <div className="flex flex-wrap items-center justify-between gap-3 mb-7">
                  <h2 className="font-serif text-2xl text-argali-ivory">{form.id ? form.title_ru || "Публикация" : "Новая публикация"}</h2>
                  {form.id && <span className="text-xs uppercase tracking-[0.14em] text-argali-gold">{STATUS_LABELS[form.status] || form.status}</span>}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div><label className={labelCls}>Тип публикации</label><select disabled={manager} className={fieldCls} value={form.post_type} onChange={(e) => setF("post_type", e.target.value)}>{Object.entries(TYPE_LABELS).map(([v,l]) => <option key={v} value={v}>{l}</option>)}</select></div>
                  <div><label className={labelCls}>Дата события / поездки</label><input disabled={manager} type="date" className={fieldCls} value={form.event_date || ""} onChange={(e) => setF("event_date", e.target.value)} /></div>
                  <div><label className={labelCls}>Название (RU)</label><input disabled={manager} className={fieldCls} value={form.title_ru || ""} onChange={(e) => setF("title_ru", e.target.value)} /></div>
                  <div><label className={labelCls}>Title (EN)</label><input disabled={manager} className={fieldCls} value={form.title_en || ""} onChange={(e) => setF("title_en", e.target.value)} /></div>
                  <div><label className={labelCls}>Место</label><input disabled={manager} className={fieldCls} placeholder="Например: Чемал, Белуха, Москва" value={form.location || ""} onChange={(e) => setF("location", e.target.value)} /></div>
                  <div><label className={labelCls}>Соревнование / организатор</label><input disabled={manager} className={fieldCls} placeholder="Если относится" value={form.organization || ""} onChange={(e) => setF("organization", e.target.value)} /></div>
                  <div><label className={labelCls}>Где проходила поездка</label><select disabled={manager} className={fieldCls} value={form.source_context} onChange={(e) => setF("source_context", e.target.value)}>{Object.entries(SOURCE_LABELS).map(([v,l]) => <option key={v} value={v}>{l}</option>)}</select></div>
                  <div><label className={labelCls}>Название другой компании</label><input disabled={manager || form.source_context !== "other_company"} className={fieldCls} value={form.source_company_name || ""} onChange={(e) => setF("source_company_name", e.target.value)} /></div>
                  <div className="md:col-span-2"><label className={labelCls}>Текст (RU)</label><textarea disabled={manager} rows={6} className={fieldCls} value={form.body_ru || ""} onChange={(e) => setF("body_ru", e.target.value)} /></div>
                  <div className="md:col-span-2"><label className={labelCls}>Text (EN) — необязательно</label><textarea disabled={manager} rows={4} className={fieldCls} value={form.body_en || ""} onChange={(e) => setF("body_en", e.target.value)} /></div>
                </div>

                {!manager && (
                  <label className="flex items-start gap-3 mt-6 text-sm text-argali-ivory/75 leading-relaxed">
                    <input type="checkbox" className="accent-argali-bronze mt-1" checked={!!form.rights_confirmed} onChange={(e) => setF("rights_confirmed", e.target.checked)} />
                    <span>Подтверждаю, что имею право публиковать эти материалы и необходимые согласия людей на фотографиях. Для фотографий детей получено согласие законного представителя.</span>
                  </label>
                )}

                <div className="mt-8 border-t border-argali-brown/30 pt-6">
                  <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                    <div><p className="eyebrow mb-1">Фотографии</p><p className="text-xs text-argali-sandstone/45">JPG / PNG / WebP / AVIF, до 12 МБ за файл.</p></div>
                    {!manager && form.id && <label className={secondaryBtn + " cursor-pointer flex items-center gap-2"}><ImagePlus size={14} /> {uploading ? "Загрузка…" : "Добавить фото"}<input disabled={uploading} type="file" accept="image/jpeg,image/png,image/webp,image/avif" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f) upload(f); }} /></label>}
                  </div>
                  {!form.id && !manager && <p className="text-sm text-argali-sandstone/50">Сначала сохраните черновик, затем появится загрузка фотографий.</p>}
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {(form.photos || []).map((photo) => <div key={photo.id} className="relative aspect-[4/3] border border-argali-brown/30 overflow-hidden"><PrivatePhoto photo={photo} />{!manager && <button onClick={() => removePhoto(photo)} className="absolute top-2 right-2 bg-black/70 text-white p-1.5"><X size={14} /></button>}</div>)}
                  </div>
                </div>

                {form.admin_note && <div className="mt-6 border border-argali-brown/30 p-4"><span className="eyebrow">Комментарий администратора</span><p className="text-sm text-argali-ivory/70 mt-2">{form.admin_note}</p></div>}

                {!manager ? (
                  <div className="flex flex-wrap gap-3 mt-8">
                    <button onClick={save} disabled={saving || form.status === "pending"} className="btn-bronze flex items-center gap-2"><Save size={15} /> {saving ? "…" : (form.id ? "Сохранить" : "Создать черновик")}</button>
                    {form.id && form.status !== "pending" && <button onClick={submit} className={secondaryBtn + " flex items-center gap-2"}><Send size={14} /> Отправить на согласование</button>}
                    {form.id && <button onClick={removePost} className={secondaryBtn + " flex items-center gap-2"}><Trash2 size={14} /> Удалить</button>}
                  </div>
                ) : (
                  <div className="mt-8">
                    <label className={labelCls}>Комментарий гиду</label>
                    <textarea rows={3} className={fieldCls} value={reviewNote} onChange={(e) => setReviewNote(e.target.value)} />
                    <div className="flex flex-wrap gap-3 mt-4">
                      <button onClick={() => review("approved")} className="btn-bronze flex items-center gap-2"><Check size={15} /> Одобрить и опубликовать</button>
                      <button onClick={() => review("rejected")} className={secondaryBtn + " flex items-center gap-2"}><X size={14} /> Отклонить</button>
                    </div>
                  </div>
                )}
              </>
            )}
          </section>
        </div>
      </main>
    </div>
  );
}